'use client';

import { create } from 'zustand';
import { api } from '@/lib/api';

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  CNY: '¥',
  EUR: '€',
  JPY: 'JP¥',
  KRW: '₩',
  HKD: 'HK$',
  TWD: 'NT$',
  GBP: '£',
  AUD: 'A$',
};

interface FxState {
  currency: string;
  rates: Record<string, number>;
  ready: boolean;
  setCurrency: (c: string) => void;
  load: () => Promise<void>;
}

export const useFxStore = create<FxState>((set, get) => ({
  currency:
    typeof window !== 'undefined'
      ? localStorage.getItem('glint-currency') || 'USD'
      : 'USD',
  rates: { USD: 1 },
  ready: false,
  setCurrency: (c: string) => {
    localStorage.setItem('glint-currency', c);
    set({ currency: c });
  },
  load: async () => {
    try {
      const r = await api.get<{ rates: Record<string, number> }>('/market-extra/fx-rates');
      set({ rates: { USD: 1, ...r.rates }, ready: true });
    } catch {
      set({ rates: { USD: 1 }, ready: true });
    }
  },
}));

/** Pure formatter: USD amount -> currency-formatted string. */
export function fmtFx(usd: number | null | undefined, rate: number, currency: string): string {
  if (usd == null || Number.isNaN(usd)) return '—';
  const v = usd * rate;
  const sym = CURRENCY_SYMBOL[currency] ?? '';
  if (currency === 'JPY' || currency === 'KRW' || currency === 'TWD') {
    return `${sym}${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  // R03/AUD03: keep meaningful significant digits for tiny non-zero prices
  // (SHIB-class quotes must never collapse to $0.00). Scale-aware like formatPrice.
  if (v === 0) return `${sym}0`;
  const abs = Math.abs(v);
  let frac = 2;
  if (abs >= 1000) frac = 0;
  else if (abs >= 100) frac = 1;
  else if (abs >= 1) frac = 4;
  else frac = 10;
  return `${sym}${v.toLocaleString('en-US', { maximumFractionDigits: frac })}`;
}

/** Price formatting in the user-selected currency. */
export function useFxPrice(usd: number | null | undefined): string {
  const { currency, rates } = useFxStore();
  return fmtFx(usd, rates[currency] ?? 1, currency);
}
